import { createModel } from '@rematch/core';
import Socket, { SocketConfig } from '@/pages/im/utils/socket';
import { getFileStatus, replaceSensitiveWords } from '@/pages/im/utils';
import {
  Message,
  FlowMessage,
  MessageEvent,
  MessageFlag,
  ResponseMessage,
  ConversationMessage,
  FileRiskMessage,
  StreamMessage
} from '@/pages/im/types/message';
import { UserInfo, BannerOption } from '@/pages/im/types/option';
import { getSocketOrigin } from '@/utils/origin';
import {
  getUserInfo,
  getCustomeHead,
  getConfig,
  getBizGray,
  checkSensitiveWords,
  getCityToChannelMap,
  getAiConfig
} from '@/api/im';

import sensor from '@/utils/sensors';
import initWatchdog from '@/utils/watchdog';
import { goLogin } from '@/utils';
import { AiConfigInfo, GreyInfo } from '@/pages/im/types';
import { CHECK_SECSITIVE_WORDS, USE_CITY_CHANNEL_MAP } from '@/config/constants';
import { EvaluateConfig } from '@/pages/im/types/evaluate';
import { RootModel } from './index';

interface OrderExtra {
  correWaterFeedback?: string;
  op?: string;
}

interface PreviewInfo {
  visible: boolean;
  src?: string;
}

type Config = SocketConfig & { token?: string };

type RatingInfo = {
  type?: 'robot' | 'manual' | 'queue' | null;
  solved?: number;
  score?: number;
  msgId?: string;
  commentType?: number;
  visible?: boolean;
  trigger?: 'leave' | 'click';
};

export interface ImState {
  messageList: Message[];
  /** 客服是否正在输入 */
  isRemoteInputing: boolean | string;
  /** 全局loading状态 */
  initLoading: boolean;
  pageLoaded: boolean;
  userInfo: UserInfo & Config;
  /** 订单列表弹窗是否显示 */
  orderListVisible: boolean;
  /** 是否正在进行人工会话 */
  isOnlineService: boolean;
  /** 进入会话时是否是人工状态 */
  isStartWithOnline?: boolean;
  ratingInfo?: RatingInfo;
  customHead?: string;
  orderExtra?: OrderExtra;
  option: Partial<BannerOption>;
  forceDisconnect?: string | boolean;
  previewInfo: PreviewInfo;
  needRefresh?: boolean;
  config: Config;
  isQueue: boolean;
  greyInfo: GreyInfo;
  sessionId?: string;
  fileRiskInfo: Record<string, string>;
  evaluateConfig?: {
    manualScore: EvaluateConfig | null;
    manualResult: EvaluateConfig | null;
    manualRemark: EvaluateConfig | null;
    robot: EvaluateConfig | null;
    message: EvaluateConfig | null;
  };
  commentedMessageIds: string[];
  aiConfig: AiConfigInfo;
}

let client: Socket;
let clearRemoteInputingTimeout: NodeJS.Timeout;

const isFlowMessage = (msg: Message) =>
  msg.event === MessageEvent.MESSAGE && (msg.data.type === 'flow' || msg.data.type === 'stream');
const isEqualMessage = (origin: Message, target: Message) =>
  isFlowMessage(origin) &&
  isFlowMessage(target) &&
  (origin as FlowMessage).data.msgId === (target as FlowMessage).data.msgId;

const checkSensitive = async (content: string) => {
  if (!CHECK_SECSITIVE_WORDS) {
    return { pass: true };
  }
  try {
    const res = await checkSensitiveWords(content || '');
    if (res.data?.hit === 1 || res.data?.hit === 2) {
      return {
        pass: false,
        black: res.data.black || [],
        replaceText: replaceSensitiveWords(
          content,
          (res.data.black || []).map(item => item.black)
        )
      };
    }
    return { pass: true };
  } catch {
    return { pass: true };
  }
};

export const im = createModel<RootModel>()({
  state: {
    messageList: [],
    isRemoteInputing: false,
    initLoading: false,
    userInfo: {},
    orderListVisible: false,
    isOnlineService: false,
    hasValidateConversation: false,
    ratingDrawerVisible: false,
    option: {},
    previewInfo: { visible: false },
    config: {},
    pageLoaded: false,
    isQueue: false,
    greyInfo: {},
    fileRiskInfo: {},
    commentedMessageIds: [],
    aiConfig: {}
  } as ImState,
  reducers: {
    setIsRemoteInputing(state, payload: boolean | string) {
      return { ...state, isRemoteInputing: payload };
    },
    setIsRemoteInputingWithMessage(state, payload: { inputing: boolean; message: Message }) {
      const { inputing, message } = payload;
      if (message.event === MessageEvent.MESSAGE && message.data.type === 'stream') {
        const hasMessageExits = state.messageList.some(msg => isEqualMessage(msg, message));
        if (!hasMessageExits) {
          clearTimeout(clearRemoteInputingTimeout);
          return { ...state, isRemoteInputing: inputing };
        }
        return state;
      }
      clearTimeout(clearRemoteInputingTimeout);
      return { ...state, isRemoteInputing: inputing };
    },
    pushMessageList({ messageList, ...state }, payload: Message) {
      return { ...state, messageList: [...messageList, payload] };
    },
    setMessageList(state, payload: Message[]) {
      return { ...state, messageList: payload };
    },
    addFlowMessage(state, payload: Message) {
      if (state.messageList.some(msg => isEqualMessage(msg, payload))) {
        return {
          ...state,
          messageList: state.messageList.map(msg => {
            if (isEqualMessage(msg, payload)) {
              return {
                ...msg,
                data: {
                  ...msg.data,
                  content:
                    ((msg as FlowMessage).data.content || '') +
                    ((payload as FlowMessage).data.content || '')
                }
              } as FlowMessage;
            }
            return msg;
          }) as Message[]
        };
      }
      return { ...state, messageList: [...state.messageList, payload] };
    },
    addStreamMessage(state, payload: Message) {
      if (state.messageList.some(msg => isEqualMessage(msg, payload))) {
        return {
          ...state,
          messageList: state.messageList.map(msg => {
            if (isEqualMessage(msg, payload)) {
              const nextContent = (payload as StreamMessage).data.content;
              const nextText = nextContent.streamAppendMsg || '';
              const currentContent = (msg as StreamMessage).data.content;
              const currentText = currentContent.streamAppendMsg || '';
              if (
                currentContent.streamType &&
                ['last', 'userBreak', 'systemBreak'].includes(currentContent.streamType)
              ) {
                return msg;
              }
              if (nextContent.streamType === 'middle' && currentText.length > nextText.length) {
                return msg;
              }
              return {
                ...msg,
                data: {
                  ...msg.data,
                  content: (payload as StreamMessage).data.content
                }
              } as StreamMessage;
            }
            return msg;
          }) as Message[]
        };
      }
      return { ...state, messageList: [...state.messageList, payload] };
    },
    widthdrawMessae({ messageList, ...state }, payload: string) {
      return {
        ...state,
        messageList: messageList.filter(
          msg => msg.event === MessageEvent.MESSAGE && String(msg.data?.msgId) !== String(payload)
        )
      };
    },
    deleteMessage({ messageList, ...state }, payload: Partial<Message>) {
      return {
        ...state,
        messageList: messageList.filter(
          msg =>
            msg.id !== payload.id ||
            msg.type !== payload.type ||
            msg.timestamp !== payload.timestamp
        )
      };
    },
    reportMessageError({ messageList, ...state }, payload: ResponseMessage) {
      return {
        ...state,
        messageList: messageList.map(msg =>
          msg.id === payload.id ? { ...msg, error: payload.data } : msg
        )
      };
    },
    updateMessage({ messageList, ...state }, payload: Partial<ConversationMessage>) {
      if (!payload?.data?.msgId) {
        return { ...state, messageList };
      }
      return {
        ...state,
        messageList: messageList.map(msg =>
          msg.event === MessageEvent.MESSAGE && msg.data.msgId === payload?.data?.msgId
            ? ({
                ...msg,
                ...payload,
                data: { ...(msg.data || {}), ...(payload.data || {}) },
                hasUpdated: true
              } as Message)
            : msg
        )
      };
    },
    sendMessageSuccess({ messageList, ...state }, payload: ResponseMessage) {
      return {
        ...state,
        messageList: messageList.map(msg =>
          msg.id === payload.id
            ? ({
                ...msg,
                data: msg.data ? { ...msg.data, msgId: payload.data.msgId } : msg.data
              } as Message)
            : msg
        )
      };
    },
    setMessageScore({ messageList, ...state }, payload: { id?: string; score?: number }) {
      return {
        ...state,
        messageList: messageList.map(msg =>
          msg.event === MessageEvent.MESSAGE &&
          msg.data.type === 'rating' &&
          msg.data.msgId === payload.id
            ? ({
                ...msg,
                data: msg.data
                  ? {
                      ...msg.data,
                      content: msg.data.content
                        ? { ...msg.data.content, score: payload.score }
                        : msg.data.content
                    }
                  : null
              } as Message)
            : msg
        )
      };
    },
    setInitLoading(state, payload: boolean) {
      return { ...state, initLoading: payload };
    },
    setPageLoaded(state, payload: boolean) {
      return { ...state, pageLoaded: payload };
    },
    setUserInfo(state, payload: UserInfo) {
      return { ...state, userInfo: { ...payload } };
    },
    setOrderListVisible(state, payload: boolean) {
      return { ...state, orderListVisible: payload };
    },
    setIsOnlineService(state, payload: boolean) {
      return { ...state, isOnlineService: payload };
    },
    setIsQueue(state, payload: boolean) {
      return { ...state, isQueue: payload };
    },
    setRatingInfo(state, payload: RatingInfo) {
      return { ...state, ratingInfo: payload };
    },
    setCustomHead(state, payload: string) {
      return { ...state, customHead: payload };
    },
    setOrderExtra(state, payload: OrderExtra | undefined) {
      return { ...state, orderExtra: payload };
    },
    setOption(state, payload: Partial<BannerOption>) {
      return { ...state, option: payload };
    },
    setForceDisconnect(state, payload: string | boolean) {
      return { ...state, forceDisconnect: payload };
    },
    setPreviewInfo(state, payload: PreviewInfo) {
      return { ...state, previewInfo: payload };
    },
    setNeedRefresh(state, payload: boolean) {
      return { ...state, needRefresh: payload };
    },
    setConfig(state, payload: Config) {
      return { ...state, config: payload };
    },
    setIsStartWithOnline(state, payload: boolean) {
      return { ...state, isStartWithOnline: payload };
    },
    setGreyInfo(state, payload: GreyInfo) {
      return { ...state, greyInfo: payload };
    },
    setSessionId(state, payload: string) {
      return { ...state, sessionId: payload };
    },
    setFileRiskInfo(state, payload: FileRiskMessage) {
      const { cmId, fileRiskStatus } = payload.data || {};
      if (!cmId) {
        return state;
      }
      if (!fileRiskStatus) {
        const info = { ...state.fileRiskInfo };
        delete info[cmId];
        return { ...state, fileRiskInfo: info };
      }
      return { ...state, fileRiskInfo: { ...state.fileRiskInfo, [cmId]: fileRiskStatus } };
    },
    setEvaluateConfig(state, payload: ImState['evaluateConfig']) {
      return { ...state, evaluateConfig: payload };
    },
    addCommentedMessageId(state, payload: string) {
      return { ...state, commentedMessageIds: [...state.commentedMessageIds, payload] };
    },
    setAiConfig(state, payload: AiConfigInfo) {
      return { ...state, aiConfig: payload };
    }
  },
  effects: dispatch => ({
    async init(config: Config) {
      const {
        initSocket,
        setInitLoading,
        setUserInfo,
        getCustomHeadImage,
        getGreyInfo,
        getAiConfigInfo,
        getOption,
        setConfig,
        setPageLoaded
      } = dispatch.im;
      sensor.init(config.from, config.channelid, config.user_type, config.from_page_id);
      setConfig(config);
      setInitLoading(true);
      let channelId = config.channelid;

      if (config.uid) {
        initSocket(config);
        return;
      }

      try {
        const res = await getUserInfo(
          {
            token: config.token,
            user_type: config.user_type,
            open_id: config.open_id,
            from: config.from
          },
          config.jwt
        );
        const { data, ret } = res;
        if (ret === 0) {
          if (!data) {
            setInitLoading(false);
            return;
          }
          window.sessionStorage.setItem('auth_token', data.auth_token || '');
          if (USE_CITY_CHANNEL_MAP && config.city_id) {
            const cityRes = await getCityToChannelMap({
              cityId: config.city_id,
              channelId: config.channelid
            }).catch(() => ({ ret: 1, data: { channelId: undefined } }));
            if (
              cityRes.ret === 0 &&
              cityRes.data.channelId &&
              cityRes.data.channelId !== config.channelid
            ) {
              channelId = cityRes.data.channelId;
            }
          }
          const userType =
            String(config.user_type) === '1'
              ? data.user_type || config.user_type
              : config.user_type;
          const socketConfig = {
            ...config,
            channelId,
            channelid: channelId,
            user_type: userType,
            uid: `user#${data.open_id}`,
            open_id: data.open_id,
            user_id: data.user_id,
            testUrl: config.testUrl,
            phone_no: data.phone_no,
            nick_name: data.nick_name || '',
            jwt: data.auth_token
          };
          setUserInfo(socketConfig);
          setPageLoaded(true);
          initSocket(socketConfig);
          getOption(socketConfig);
          getCustomHeadImage(socketConfig);
          if (channelId) {
            getGreyInfo({ channelId, cityId: socketConfig.city_id });
            getAiConfigInfo(channelId);
          }
          if (String(userType) !== String(config.user_type)) {
            setConfig({ ...config, user_type: userType });
          }

          sensor.login(data.user_id ?? '0');
          initWatchdog({ id: data.user_id, type: 'user_id' });
        } else {
          // toast.warning(res.msg || 'token 已过期');
          goLogin(config.user_type);
          setInitLoading(false);
        }
      } catch (err) {
        return null;
      }
    },
    async initSocket(config: SocketConfig) {
      const {
        setInitLoading,
        setIsRemoteInputing,
        pushMessageList,
        addFlowMessage,
        addStreamMessage,
        setIsOnlineService,
        widthdrawMessae,
        setOrderListVisible,
        setOrderExtra,
        setForceDisconnect,
        setIsStartWithOnline,
        setIsQueue,
        setSessionId,
        setFileRiskInfo,
        setIsRemoteInputingWithMessage,
        updateMessage
      } = dispatch.im;
      client = new Socket(config.testUrl ?? `${getSocketOrigin()}/websocket`, {
        ...config,
        onConnectSuccess: ({ isRobot, isQueue, sessionId }) => {
          setIsOnlineService(!isRobot);
          setIsStartWithOnline(!isRobot || isQueue);
          setInitLoading(false);
          setIsQueue(isQueue);
          setSessionId(sessionId as string);
        },
        onConnectError: () => {
          setInitLoading(false);
        }
      });
      client.onMessage = message => {
        if (message.event === MessageEvent.FILE_RISK) {
          setFileRiskInfo(message);
          return;
        }
        if (message.event === MessageEvent.REPLACE) {
          updateMessage({ event: MessageEvent.MESSAGE, data: message.data } as ConversationMessage);
          return;
        }
        if (message.event === MessageEvent.MESSAGE) {
          const { data } = message;

          // 客服发送消息命中敏感词，不在用户侧展示
          if (data.flag === MessageFlag.SENSITIVE_WORDS) {
            return;
          }

          if (data.flag === MessageFlag.INPUTING_MSG) {
            setIsRemoteInputing(data.from || true);

            clearTimeout(clearRemoteInputingTimeout);
            clearRemoteInputingTimeout = setTimeout(() => {
              setIsRemoteInputing(false);
            }, 5 * 1000);
            return;
          }

          if (data.flag === MessageFlag.ROBOT_INPUTING_MSG) {
            setIsRemoteInputing(data.from || true);

            clearTimeout(clearRemoteInputingTimeout);
            clearRemoteInputingTimeout = setTimeout(() => {
              setIsRemoteInputing(false);
            }, 10 * 1000);
            return;
          }

          if (data.type === 'invitation' && data.from?.startsWith('robot')) {
            setOrderListVisible(true);
            if (
              data.type === 'invitation' &&
              data.content.cardInfo &&
              data.content.cardInfo.correWaterFeedback
            ) {
              const { correWaterFeedback, op } = data.content.cardInfo;
              setOrderExtra({ correWaterFeedback, op });
            }
          }

          if (data.flag === MessageFlag.UN_SEND_MSG) {
            if (data.msgId) {
              widthdrawMessae(data.msgId);
            }
          }

          if (data.flag === MessageFlag.ACCEPT_MSG || data.from?.startsWith('kefu')) {
            setIsOnlineService(true);
            setIsQueue(false);
          }

          if (data.flag === MessageFlag.SESSION_CLOSED_MSG) {
            setIsOnlineService(false);
          }

          if (data.flag !== MessageFlag.ONLINE_MSG && data.from?.startsWith('robot')) {
            setIsOnlineService(false);
          }

          if (data.flag === MessageFlag.JOIN_QUEUE) {
            setIsQueue(true);
          }

          if (data.flag === MessageFlag.QUEUE_OVERTIME_CLOSE) {
            setIsQueue(false);
          }

          if (data.type === 'stream') {
            setIsRemoteInputingWithMessage({ inputing: false, message });
          } else {
            setIsRemoteInputing(false);
            clearTimeout(clearRemoteInputingTimeout);
          }

          if (data.type === 'flow') {
            addFlowMessage(message);
          } else if (data.type === 'stream') {
            addStreamMessage(message);
          } else {
            pushMessageList(message);
          }
        }

        if (message.event === MessageEvent.DISCONNECT) {
          if (message.data?.message) {
            return setForceDisconnect(message.data.message);
          }
          return setForceDisconnect(true);
        }
      };
    },
    async request(message: Partial<Message>) {
      return client?.request(message);
    },
    async send(message: string | Partial<Message>) {
      if (!client || client?.closed) {
        dispatch.im.setNeedRefresh(true);
        return;
      }
      let messageItem: Partial<Message>;
      if (typeof message === 'string') {
        const { pass, replaceText } = await checkSensitive(message);
        if (pass) {
          messageItem = {
            event: MessageEvent.MESSAGE,
            data: { type: 'text', content: message }
          };
        } else {
          messageItem = {
            event: MessageEvent.MESSAGE,
            data: {
              type: 'text',
              content: message,
              sensitiveContent: replaceText,
              flag: MessageFlag.SENSITIVE_WORDS
            }
          };
        }
      } else {
        messageItem = message;
      }
      const item = client?.send(
        messageItem,
        messageItem.event === MessageEvent.MESSAGE
          ? {
              onError: msg => {
                dispatch.im.reportMessageError(msg);
              },
              onSuccess: msg => {
                dispatch.im.sendMessageSuccess(msg);
              }
            }
          : undefined
      );
      if (
        item &&
        item.event === MessageEvent.MESSAGE &&
        !['image', 'voice', 'video'].includes(item.data.type as string)
      ) {
        dispatch.im.pushMessageList(
          item.data.type === 'text'
            ? {
                ...item,
                type: 1,
                data: {
                  ...item.data,
                  content: (item.data?.content || '')
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#x27')
                    .replace(/\//g, '&#x2F')
                }
              }
            : { ...item, type: 1 }
        );
      }
    },
    resend(message: Partial<Message>) {
      const { send, deleteMessage } = dispatch.im;
      const { id, type, timestamp, prev, ...msg } = message;
      send(msg);
      deleteMessage(message);
    },
    async sendFile(message: Partial<Message>, rootState) {
      if (message.event !== MessageEvent.MESSAGE) {
        return;
      }
      const { messageList } = rootState.im;
      const currentMessage = messageList.find(
        msg => msg.event === MessageEvent.MESSAGE && msg.data.cmId === message.data?.cmId
      );
      if (getFileStatus(message) === 'done') {
        dispatch.im.send(message);
      }
      if (currentMessage) {
        dispatch.im.setMessageList(
          messageList.map(msg =>
            msg.event === MessageEvent.MESSAGE && msg.data.cmId === message.data?.cmId
              ? ({ ...currentMessage, ...message } as Message)
              : msg
          )
        );
      } else {
        const baseMessage = client?.generateBaseMessage();
        dispatch.im.pushMessageList({ ...baseMessage, ...message } as Message);
      }
    },
    close() {
      client?.close();
    },
    closeAsync() {
      return client?.closeAsync();
    },
    disconnect() {
      client?.disconnect();
    },
    sendAck(msgIds: string[]) {
      client?.sendAck(msgIds);
    },
    async getCustomHeadImage(config: SocketConfig) {
      const { phone_no, user_id, user_type } = config;
      try {
        const res = await getCustomeHead({
          uid: phone_no,
          user_id,
          identity: user_type,
          os: /iPhone|iPad|iPod|iOS/i.test(window.navigator.userAgent) ? 'IOS' : 'ANDROID'
        });
        if (res.data) {
          dispatch.im.setCustomHead(res.data);
        }
      } catch (err) {
        return null;
      }
    },
    async getGreyInfo(data: { channelId: string; cityId?: string }) {
      try {
        const res = await getBizGray(data);
        if (res.data) {
          dispatch.im.setGreyInfo(res.data);
        }
      } catch (err) {
        return null;
      }
    },
    async getAiConfigInfo(channelId: string) {
      try {
        const res = await getAiConfig(channelId);
        if (res.data) {
          dispatch.im.setAiConfig(res.data.aiConfig || {});
        }
      } catch (err) {
        return null;
      }
    },
    async getOption(config: SocketConfig) {
      const { sysnum, source, channelid, open_id, uid, user_type } = config;
      try {
        const res = await getConfig({
          sysNum: sysnum,
          source,
          channelFlag: channelid,
          partnerId: open_id,
          noticeReq: {
            channelId: channelid,
            uid,
            userType: user_type
          }
        });
        if (res.data) {
          const { announceTopFlag, announceClickUrl, announceMsg = '', noticeInfo } =
            res.data || {};
          if (noticeInfo) {
            dispatch.im.setOption(noticeInfo);
            return;
          }
          const regx = /[\s\S]*<!-- title -->([\s\S]*)<!-- title-end -->[\s\S]*<!-- content -->([\s\S]*)<!-- content-end -->[\s\S]*/;
          const parts = regx.exec(announceMsg) || [];
          const title = announceTopFlag === '1' ? announceMsg : parts[1];
          dispatch.im.setOption({
            status: title ? 1 : 2,
            type: announceTopFlag === '0' ? 1 : 2,
            title,
            content: parts ? parts[2] : '',
            address: announceClickUrl
          });
        }
      } catch (err) {
        return null;
      }
    }
  })
});

export default im;
